import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorFallbackProps {
    title?: string;
    message?: string;
    onRetry?: () => void;
    showRetry?: boolean;
    compact?: boolean;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
    title,
    message,
    onRetry,
    showRetry = true,
    compact = false
}) => {
    const { t } = useTranslation('common');
    const displayTitle = title ?? t('errors.somethingWrong');
    const displayMessage = message ?? t('errors.unexpectedError');

    if (compact) {
        return (
            <div className="flex items-center justify-center p-4 border border-red-200 rounded-lg bg-red-50">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
                <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{displayTitle}</p>
                    <p className="text-xs text-red-600">{displayMessage}</p>
                </div>
                {showRetry && onRetry && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onRetry}
                        className="ml-3"
                    >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        {t('buttons.retry')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-xl">{displayTitle}</CardTitle>
                <CardDescription>{displayMessage}</CardDescription>
            </CardHeader>
            <CardContent>
                {showRetry && onRetry && (
                    <Button
                        onClick={onRetry}
                        className="w-full"
                        variant="default"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('errors.tryAgain')}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default ErrorFallback;
